import type { MouseEvent } from "react";
import { useCallback, useEffect } from "react";
import { Navigate, useHref, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Link,
  Tabs,
  Typography
} from "@dealer-center/core-components";
import { deriveEnvironmentName } from "@dealer-center/core-enviroment";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import { OEM_LOADER_URLS } from "../loaderUrls";
import type { OemNavigation } from "../OemElement";
import { OemElement } from "../OemElement";
import type { OemNavigateDetail, OemViewGrammar } from "../OemElement/contract";
import {
  SI_COMPLIANCE_NAVIGATE_EVENT,
  SI_OEM_COMPLIANCE_TAG,
  SI_OEM_SCORING_TAG,
  SI_SCORING_NAVIGATE_EVENT,
  SI_SEO_GEO_TAG,
  SI_SEO_NAVIGATE_EVENT
} from "../OemElement/contract";

/**
 * The OEM Compliance Center hosts three web components published by the Site Intelligence UI
 * and loaded from their own origins at runtime, one per tab. The bundles carry their own
 * environment, API base URL and build version; what this page owns is the URL. Each element's
 * screen is `/oemComplianceCenter/<tab>/<path>?<query>`, in the bundle's own path grammar, so
 * Back, Forward, reload and a pasted link all land on the same screen.
 */
export const OEM_COMPLIANCE_CENTER_BASE = "/oemComplianceCenter";
export const OEM_COMPLIANCE_CENTER_NOT_FOUND_TESTID =
  "oem-compliance-center-not-found";

// `landing` is the screen a bare tab opens: compliance's journey starts at the brand list, and
// scoring's at its Scoring Check screen. `si-seo-geo` has no path grammar at all; its screen is
// its query string.
const TABS = [
  {
    value: "compliance",
    label: "Compliance",
    tagName: SI_OEM_COMPLIANCE_TAG,
    navigateEvent: SI_COMPLIANCE_NAVIGATE_EVENT,
    landing: "brands"
  },
  {
    value: "scoring",
    label: "Scoring",
    tagName: SI_OEM_SCORING_TAG,
    navigateEvent: SI_SCORING_NAVIGATE_EVENT,
    landing: "check"
  },
  {
    value: "seo",
    label: "SEO",
    tagName: SI_SEO_GEO_TAG,
    navigateEvent: SI_SEO_NAVIGATE_EVENT,
    landing: undefined
  }
] as const;

const landingPath = ({ value, landing }: (typeof TABS)[number]) =>
  `${OEM_COMPLIANCE_CENTER_BASE}/${value}${landing === undefined ? "" : `/${landing}`}`;

const withQuery = (path: string, params: URLSearchParams) => {
  // `toString`, not `Object.fromEntries`: a repeated key is one filter with several values.
  const query = params.toString();
  return query === "" ? path : `${path}?${query}`;
};

export const PagesOemComplianceCenterUiDisplay = () => {
  const loaderUrls = OEM_LOADER_URLS[deriveEnvironmentName()];
  const { "ddc.oem-compliance-center.enable": isEnabled } = useCC9Flags();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // Anchors inside the elements are resolved by the browser, so they need the app's basename.
  const baseHref = useHref(OEM_COMPLIANCE_CENTER_BASE);

  useEffect(() => {
    document.title = "OEM Compliance Center";
  }, []);

  // The raw pathname rather than the `*` param, which the router has already decoded: the
  // bundle's `viewForPath` decodes its own segments, and decoding twice corrupts a `%25`.
  const [tabValue = "", ...segments] = pathname
    .slice(OEM_COMPLIANCE_CENTER_BASE.length + 1)
    .split("/");
  const path = segments.join("/");
  const tab = TABS.find(candidate => candidate.value === tabValue);

  // Memoised on the location's parts: every property write re-renders the element.
  const navigation = useCallback(
    (bundle: unknown): OemNavigation | undefined => {
      if (tab === undefined) return undefined;
      const tabPath = `${OEM_COMPLIANCE_CENTER_BASE}/${tab.value}`;
      const params = new URLSearchParams(search);
      if (tab.landing === undefined) {
        if (path !== "") return undefined;
        return {
          params,
          // The Scoring tab's Scoring Check screen, which reads the same two parameters.
          hrefForScoringCheck: (brand, url) =>
            withQuery(
              `${baseHref}/scoring/check`,
              new URLSearchParams({ brand, url })
            ),
          navigateEvent: tab.navigateEvent,
          onNavigate: (detail: OemNavigateDetail) =>
            navigate(withQuery(tabPath, detail.params), {
              replace: detail.replace
            })
        };
      }
      const { pathForView, viewForPath } = bundle as OemViewGrammar;
      const view = viewForPath(path);
      if (view === undefined) return undefined;
      return {
        view,
        params,
        hrefFor: (target, query) =>
          `${baseHref}/${tab.value}/${pathForView(target, query)}`,
        hrefForSeoSites:
          tab.value === "compliance"
            ? brand =>
                withQuery(
                  `${baseHref}/seo`,
                  new URLSearchParams({ view: "sites", brand })
                )
            : undefined,
        hrefForBrandScoring:
          tab.value === "compliance"
            ? brand =>
                withQuery(
                  `${baseHref}/scoring/check`,
                  new URLSearchParams({ brand })
                )
            : undefined,
        navigateEvent: tab.navigateEvent,
        onNavigate: (detail: OemNavigateDetail) =>
          navigate(
            withQuery(`${tabPath}/${pathForView(detail.view)}`, detail.params),
            { replace: detail.replace }
          )
      };
    },
    [tab, path, search, baseHref, navigate]
  );

  // `!== true`, not `=== false`: `undefined` means LaunchDarkly has not answered yet, and this
  // page must not render in that window. Hiding the nav item is not enough on its own — the
  // route is reachable by typing the URL.
  if (isEnabled !== true) return <Navigate replace to="/" />;

  // Absolute targets: a relative one inside this splat route resolves against the matched
  // segment rather than the page, and loops.
  if (tab === undefined)
    return <Navigate replace to={`${landingPath(TABS[0])}${search}`} />;
  if (tab.landing !== undefined && path === "")
    return <Navigate replace to={`${landingPath(tab)}${search}`} />;

  const notFound = (
    <Alert
      data-testid={OEM_COMPLIANCE_CENTER_NOT_FOUND_TESTID}
      role="alert"
      type="info"
    >
      There is no such screen in the OEM Compliance Center.{" "}
      <Link href={`${baseHref}/${tab.value}`}>Go to {tab.label}</Link>
    </Alert>
  );

  // Straight to the landing screen: the bare tab's redirect would unmount the tab strip and
  // drop keyboard focus to the body.
  const handleActivate = (value: string) => {
    const target = TABS.find(candidate => candidate.value === value);
    if (target !== undefined) navigate(landingPath(target));
  };

  // A link to another tab's screen is a plain anchor, because an element can only `go` to its
  // own views; followed natively, it would reload the whole of Dealer Center.
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    const { nativeEvent } = event;
    if (
      nativeEvent.defaultPrevented ||
      nativeEvent.button !== 0 ||
      nativeEvent.metaKey ||
      nativeEvent.ctrlKey ||
      nativeEvent.shiftKey ||
      nativeEvent.altKey
    )
      return;
    // `composedPath`, because an element's anchor sits inside its shadow root.
    const anchor = nativeEvent
      .composedPath()
      .find(
        (node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement
      );
    if (
      anchor === undefined ||
      !["", "_self"].includes(anchor.target) ||
      anchor.hasAttribute("download")
    )
      return;
    const url = new URL(anchor.href);
    if (
      url.origin !== window.location.origin ||
      !url.pathname.startsWith(`${baseHref}/`)
    )
      return;
    event.preventDefault();
    navigate(
      `${OEM_COMPLIANCE_CENTER_BASE}${url.pathname.slice(baseHref.length)}${url.search}`
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
        <Typography variant={"h1"}>OEM Compliance Center</Typography>
      </Box>
      {/* 0, not the 64px header: Dealer Center scrolls the page in its own container below the
          header, so the elements' sticky chrome is measured from that container's top. */}
      <Box sx={{ "--si-host-offset-top": "0px" }} onClick={handleClick}>
        <Tabs
          tabs={TABS.map(candidate => ({
            active: candidate === tab,
            // Only the active tab mounts its element, so a visit loads one bundle, not three.
            // The `key` gives each tab its own instance rather than retagging one. No `session`:
            // with none set, each element reads Dealer Center's `JWTAuth` cookie itself.
            component:
              candidate === tab ? (
                <OemElement
                  key={candidate.value}
                  loaderUrl={loaderUrls[candidate.value]}
                  navigation={navigation}
                  notFound={notFound}
                  tagName={candidate.tagName}
                />
              ) : (
                <></>
              ),
            label: candidate.label,
            value: candidate.value
          }))}
          onActivate={handleActivate}
        />
      </Box>
    </Box>
  );
};
